<template>
  <div>
    <page-loading
      v-if="loading"
      message="Carregando clientes"
      subtitle="Montando a lista e o resumo da base"
    />
    <template v-else>
      <div class="clients-actions mb-4">
        <v-btn
          outlined
          color="primary"
          class="mr-2"
          @click="handleImportClients()"
        >
          <v-icon left>mdi-file-upload-outline</v-icon>
          Importar clientes
        </v-btn>
        <v-btn dark color="primary" depressed @click="handleNewClient()">
          <v-icon left>mdi-plus</v-icon>
          Novo Cliente
        </v-btn>
      </div>

      <section class="page-summary mb-4">
        <h2 class="br-section-title">Resumo</h2>
        <v-row dense class="page-summary__grid">
          <v-col cols="12" sm="6" md="4">
            <partner-summary-metric-card
              title="Total de clientes"
              :value="clients.length"
              icon="mdi-account-group-outline"
              variant="network"
              tooltip="Todos os clientes cadastrados na sua conta."
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <partner-summary-metric-card
              title="Clientes ativos"
              :value="handleGetClientsActive()"
              icon="mdi-check-decagram-outline"
              variant="finance"
              tooltip="Clientes com status ativo."
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <partner-summary-metric-card
              title="Clientes pendentes"
              :value="handleGetClientsPending()"
              icon="mdi-account-clock-outline"
              variant="amber"
              tooltip="Clientes aguardando conclusão ou aprovação."
            />
          </v-col>
        </v-row>
      </section>

      <data-table-card
        title="Lista de clientes"
        icon="mdi-account-group-outline"
        :headers="headers"
        :items="clients"
        :items-per-page="10"
        :footer-props="{ 'items-per-page-text': 'clientes por página' }"
        :header-props="{ sortByText: 'Filtrar por' }"
        searchable
        :search.sync="search"
        search-label="Pesquise pelo nome, CPF, telefone ou email"
        show-count
        no-data-text="Nenhum cliente encontrado"
      >
        <template v-slot:[`item.document`]="{ item }">
          {{ formatDocument(item.document) }}
        </template>
        <template v-slot:[`item.phone`]="{ item }">
          {{ phoneNumberFormat(item.phone) }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <status-badge-vue :status="item.status" />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="primary"
                v-bind="attrs"
                v-on="on"
                @click="handleDetailClient(item.id)"
              >
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            Editar cliente
          </v-tooltip>
        </template>
        <template v-slot:no-data>
          <empty-state
            icon="mdi-account-off-outline"
            title="Nenhum cliente cadastrado"
            description="Cadastre seu primeiro cliente para começar a vender com cashback."
            variant="brand"
          >
            <v-btn dark color="primary" depressed @click="handleNewClient()">
              <v-icon left>mdi-plus</v-icon>
              Novo Cliente
            </v-btn>
          </empty-state>
        </template>
      </data-table-card>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import StatusBadgeVue from "@/components/statusBadge/StatusBadge.vue";
import PartnerSummaryMetricCard from "@/components/summary/PartnerSummaryMetricCard.vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import DataTableCard from "@/components/ui/DataTableCard.vue";
import EmptyState from "@/components/ui/EmptyState.vue";

import { ClientType } from "@/types/client/Client";
import { GetAllClients } from "@/services/clients/GetAllClients";
import { format } from "date-fns";

interface SortItem {
  name: string;
  phone: string;
  email: string;
  document: string;
}

export default defineComponent({
  components: {
    DataTableCard,
    EmptyState,
    PageLoading,
    PartnerSummaryMetricCard,
    StatusBadgeVue,
  },
  data() {
    return {
      mobileBreakpoint: 600,
      loading: true,
      clients: [] as ClientType[],
      search: "",
      sortBy: [] as ReadonlyArray<SortItem>,
      sortDesc: false,
      noDataText: "Nenhum dado disponível",
      headers: [
        { text: "Nome", value: "name" },
        { text: "Documento", value: "document" },
        { text: "Telefone", value: "phone" },
        { text: "Email", value: "email" },
        { text: "Situação", value: "status" },
        { text: "Ações", value: "actions", sortable: false, align: "center" },
      ],
    };
  },
  mounted() {
    this.handleLoadClients();
  },
  methods: {
    async handleLoadClients() {
      this.loading = true;
      const getAllClients = new GetAllClients();
      const clients = await getAllClients.execute();
      this.clients = clients;
      this.loading = false;
    },
    handleNewClient() {
      this.$router.push({
        name: "clientForm",
      });
    },
    handleImportClients() {
      this.$router.push({
        name: "clientsImport",
      });
    },
    handleDetailClient(id: string) {
      this.$router.push({
        name: "clientDetail",
        params: { id },
      });
    },
    handleGetClientsActive(): number {
      return this.clients.filter((client) => client.status === "ACTIVE").length;
    },
    handleGetClientsPending(): number {
      return this.clients.filter((client) => client.status === "PENDING").length;
    },
    formatDocument(value: string): string {
      if (!value) return "";
      const digits = value.replace(/\D/g, "");
      if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      if (digits.length === 14) {
        return digits.replace(
          /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
          "$1.$2.$3/$4-$5",
        );
      }
      return value;
    },
    phoneNumberFormat: (value: string): string => {
      if (!value) return "";
      const digits = value.replace(/\D/g, "");
      if (digits.length === 11) {
        return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
      }
      if (digits.length === 10) {
        return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
      }
      return value;
    },
    formatDate(value: string): string {
      if (!value) return "";
      return format(new Date(value), "dd/MM/yyyy");
    },
  },
});
</script>

<style scoped lang="scss">
.clients-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
